import {ChatAccessDeniedReason, SubscriptionDetails} from './crud-helper/models'
import {getSubscriptionLifecycle} from './subscriptionLifecycle'

/** Subscription module that licenses the OmicsAI Agent (same module that licenses Engine API keys). */
export const CHAT_LICENSE_MODULE = 'ENGINE'

export interface ChatAccess {
  /** Full use: the panel opens and new turns may start (cloud units permitting). */
  canUse: boolean
  /**
   * The panel opens and past conversations can be browsed. True whenever `canUse` is, and also for
   * a subscription that licenses ENGINE and expired within the grace period, so those users keep
   * read-only access to their history. Once it lapses (see `getSubscriptionLifecycle`) the chat
   * is off.
   */
  canView: boolean
  reason: ChatAccessDeniedReason | null
}

const deny = (reason: ChatAccessDeniedReason): ChatAccess => ({
  canUse: false,
  canView: false,
  reason,
})

/**
 * Who may use the OmicsAI Agent: an enabled, unexpired OmicsBox subscription that licenses ENGINE.
 * Cloud-unit balance is checked separately (it blocks new turns, not the panel).
 */
export function computeChatAccess(
  subscriptionId: string | null,
  details: SubscriptionDetails | null,
  error: string | null,
  now: number = Date.now()
): ChatAccess {
  if (!subscriptionId || error || !details) {
    return deny('no-subscription')
  }
  if (details.productName?.toUpperCase() !== 'OMICSBOX') {
    return deny('wrong-product')
  }
  if (!details.isEnabled) {
    return deny('not-enabled')
  }
  const licensesChat = details.modules?.includes(CHAT_LICENSE_MODULE) ?? false
  const lifecycle = getSubscriptionLifecycle(details.endDate, now)
  if (lifecycle !== 'active') {
    return {canUse: false, canView: lifecycle === 'expired' && licensesChat, reason: 'expired'}
  }
  if (!licensesChat) {
    return deny('no-engine')
  }
  return {canUse: true, canView: true, reason: null}
}
